import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "./ui/select";

// Sugerencia de departamento según la categoría del control (no es un automatismo).
const CATEGORY_KEYWORDS = {
  technological: ["it", "sistemas", "tecnolog", "informát", "ti"],
  people: ["rrhh", "recursos humanos", "personas", "talento"],
  physical: ["servicios generales", "instalaciones", "mantenimiento", "seguridad física", "it", "sistemas"],
  organizational: ["dirección", "direccion", "gerencia", "compliance", "cumplimiento", "calidad"],
};

function unitSuggested(unitName, category) {
  if (!category) return false;
  const kws = CATEGORY_KEYWORDS[category] || [];
  const n = (unitName || "").toLowerCase();
  return kws.some((k) => n.includes(k));
}

/**
 * Selector de responsable basado en PERSONAS (people), agrupadas por departamento.
 * value = person_id ("" o "none" = sin asignar). Compatibilidad: si hay un legacyUserLabel
 * (owner_user_id antiguo) y aún no se ha elegido persona, se muestra como opción.
 */
export default function OwnerPersonSelect({ value, onChange, controlCategory, legacyUserLabel, testid = "owner-person-select" }) {
  const [people, setPeople] = useState([]);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, u] = await Promise.all([api.get("/people?active=true"), api.get("/org-units")]);
        if (cancelled) return;
        setPeople(Array.isArray(p.data) ? p.data : []);
        setUnits(Array.isArray(u.data) ? u.data : []);
      } catch { /* sin estructura aún */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const groups = useMemo(() => {
    const byUnit = {};
    const noUnit = [];
    people.forEach((p) => { (p.org_unit_id ? (byUnit[p.org_unit_id] ||= []) : noUnit).push(p); });
    const ordered = [...units].sort((a, b) => {
      const sa = unitSuggested(a.name, controlCategory) ? 0 : 1;
      const sb = unitSuggested(b.name, controlCategory) ? 0 : 1;
      return sa - sb || a.name.localeCompare(b.name);
    });
    return { ordered, byUnit, noUnit };
  }, [people, units, controlCategory]);

  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Sin asignar" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Sin asignar</SelectItem>
        {legacyUserLabel && !value && <SelectItem value="__legacy" disabled>{legacyUserLabel} (usuario actual)</SelectItem>}
        {groups.ordered.map((u) => {
          const list = groups.byUnit[u.id] || [];
          if (list.length === 0) return null;
          const sug = unitSuggested(u.name, controlCategory);
          return (
            <SelectGroup key={u.id}>
              <SelectLabel className="flex items-center gap-1">{u.name}{sug && <span className="text-primary text-[10px] font-normal">· sugerido</span>}</SelectLabel>
              {list.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.job_title ? ` — ${p.job_title}` : ""}</SelectItem>)}
            </SelectGroup>
          );
        })}
        {groups.noUnit.length > 0 && (
          <SelectGroup>
            <SelectLabel>Sin departamento</SelectLabel>
            {groups.noUnit.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.job_title ? ` — ${p.job_title}` : ""}</SelectItem>)}
          </SelectGroup>
        )}
        {people.length === 0 && <SelectItem value="__empty" disabled>Añade personas en el paso «Quién se encarga de qué»</SelectItem>}
      </SelectContent>
    </Select>
  );
}
